import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, View } from 'react-native';

import { Button, Dialog, DialogButton, Text } from '@/components/ui';
import {
  deleteBudget,
  deleteCategory,
  fetchAllTransactions,
  fetchBudgets,
  fetchCategories,
  fetchCategoryUsage,
  putBudget,
} from '@/lib/finance/api';
import {
  addableBudgetOptions,
  budgetableOptions,
  categoryDeleteWarning,
  visibleBudgetOptions,
} from '@/lib/finance/budget-rows';
import { ensureCategoriesFromLabels } from '@/lib/finance/ensure-categories';
import { categoryOptions, formatUsd } from '@/lib/finance/format';
import { buildResolvedBudgetRows, CREATE_CATEGORY_SENTINEL_ID } from '@/lib/finance/import';
import { addMonths, monthLabel, monthLabelShort, monthStart } from '@/lib/finance/month';
import { unbudgetedSpend } from '@/lib/finance/report';
import type { Budget, Category, FinanceCategoryKind, Transaction } from '@/lib/finance/types';

type Draft = { min: string; max: string; notes: string };

function toDraft(budget: Budget | undefined): Draft {
  return {
    min: budget?.minUsd != null ? String(budget.minUsd) : '',
    max: budget?.maxUsd != null ? String(budget.maxUsd) : '',
    notes: budget?.notes ?? '',
  };
}

/** Compact USD band for the read-only neighbour-month reference columns. */
function bandText(budget: Budget | undefined): string {
  if (!budget) return '';
  const min = budget.minUsd ?? 0;
  const max = budget.maxUsd ?? 0;
  if (min === 0 && max === 0) return '';
  if (min === max) return formatUsd(max);
  if (min === 0) return `≤ ${formatUsd(max)}`;
  return `${formatUsd(min)}–${formatUsd(max)}`;
}

function TrashIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      width={14}
      height={14}
      aria-hidden
      className="block"
    >
      <path
        d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M4 7h16M18 7v12a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V7M10 11v6M14 11v6"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function BudgetsTab({ month }: { month: string }) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [budgets, setBudgets] = useState<Map<number, Budget>>(new Map());
  const [prevBudgets, setPrevBudgets] = useState<Map<number, Budget>>(new Map());
  const [nextBudgets, setNextBudgets] = useState<Map<number, Budget>>(new Map());
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [drafts, setDrafts] = useState<Map<number, Draft>>(new Map());
  const [visibleIds, setVisibleIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const monthIso = monthStart(month);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const prevIso = monthStart(addMonths(month, -1));
      const nextIso = monthStart(addMonths(month, 1));
      const [categoryRows, budgetRows, transactionRows, prevRows, nextRows] = await Promise.all([
        fetchCategories(),
        fetchBudgets(monthIso),
        fetchAllTransactions({ month: monthIso }),
        fetchBudgets(prevIso),
        fetchBudgets(nextIso),
      ]);
      setCategories(categoryRows);
      const budgetMap = new Map(budgetRows.map((b) => [b.categoryId, b]));
      const ids = new Set(budgetRows.map((b) => b.categoryId));
      setBudgets(budgetMap);
      setVisibleIds(ids);
      setPrevBudgets(new Map(prevRows.map((b) => [b.categoryId, b])));
      setNextBudgets(new Map(nextRows.map((b) => [b.categoryId, b])));
      setTransactions(transactionRows);
      setDrafts(new Map([...ids].map((id) => [id, toDraft(budgetMap.get(id))])));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load budgets');
    } finally {
      setLoading(false);
    }
  }, [month, monthIso]);

  useEffect(() => {
    void load();
  }, [load]);

  const budgetable = budgetableOptions(categories);
  const rows = visibleBudgetOptions(budgetable, visibleIds);
  const addable = addableBudgetOptions(budgetable, visibleIds);

  const unbudgeted = useMemo(
    () => unbudgetedSpend(categories, [...budgets.values()], transactions),
    [categories, budgets, transactions],
  );
  const unbudgetedTotal = useMemo(
    () => unbudgeted.reduce((sum, row) => sum + row.actualUsd, 0),
    [unbudgeted],
  );

  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [importBusy, setImportBusy] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  const [addOpen, setAddOpen] = useState(false);
  const [addExistingId, setAddExistingId] = useState('');
  const [addNewLabel, setAddNewLabel] = useState('');
  const [addNewKind, setAddNewKind] = useState<FinanceCategoryKind>('expense');
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const [removeOpen, setRemoveOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<{
    id: number;
    label: string;
    transactionCount: number;
  } | null>(null);

  const categoryByLabel = useMemo(
    () => new Map(categoryOptions(categories).map((o) => [o.label.toLowerCase(), o.id])),
    [categories],
  );
  const importResolved = useMemo(
    () =>
      importText.trim()
        ? buildResolvedBudgetRows(importText, categoryByLabel)
        : { mapping: {}, rows: [] },
    [importText, categoryByLabel],
  );
  const importValid = importResolved.rows.filter((r) => r.errors.length === 0);
  const importSkipped = importResolved.rows.length - importValid.length;
  const importNewCategories = useMemo(() => {
    const labels = new Set<string>();
    for (const row of importValid) {
      if (row.categoryId === CREATE_CATEGORY_SENTINEL_ID) labels.add(row.categoryLabel);
    }
    return labels.size;
  }, [importValid]);

  function setDraft(categoryId: number, patch: Partial<Draft>) {
    setDrafts((prev) => {
      const next = new Map(prev);
      next.set(categoryId, {
        ...(next.get(categoryId) ?? { min: '', max: '', notes: '' }),
        ...patch,
      });
      return next;
    });
  }

  async function commit(categoryId: number) {
    const draft = drafts.get(categoryId) ?? { min: '', max: '', notes: '' };
    const min = draft.min.trim() === '' ? 0 : Number(draft.min);
    const max = draft.max.trim() === '' ? 0 : Number(draft.max);
    if (!Number.isFinite(min) || !Number.isFinite(max) || min < 0 || max < 0) {
      setError('Budget amounts must be non-negative numbers');
      return;
    }
    if (max < min) {
      setError('Max must be greater than or equal to min');
      return;
    }

    const existing = budgets.get(categoryId);
    const isEmpty = min === 0 && max === 0 && draft.notes.trim() === '';
    setBusy(true);
    setError(null);
    try {
      if (isEmpty) {
        if (existing) {
          await deleteBudget(monthIso, categoryId);
          setBudgets((prev) => {
            const next = new Map(prev);
            next.delete(categoryId);
            return next;
          });
        }
        return;
      }
      const saved = await putBudget({
        month: monthIso,
        categoryId,
        minUsd: min,
        maxUsd: max,
        notes: draft.notes.trim() || null,
      });
      setBudgets((prev) => new Map(prev).set(categoryId, saved));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save budget');
    } finally {
      setBusy(false);
    }
  }

  async function quickBudget(categoryId: number, recommendedMax: number) {
    setBusy(true);
    setError(null);
    try {
      const saved = await putBudget({
        month: monthIso,
        categoryId,
        minUsd: 0,
        maxUsd: recommendedMax,
        notes: null,
      });
      setBudgets((prev) => new Map(prev).set(categoryId, saved));
      setVisibleIds((prev) => new Set(prev).add(categoryId));
      setDrafts((prev) =>
        new Map(prev).set(categoryId, { min: '0', max: String(recommendedMax), notes: '' }),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add budget');
    } finally {
      setBusy(false);
    }
  }

  async function runBudgetImport() {
    if (importValid.length === 0) {
      setImportError('No valid rows to import.');
      return;
    }
    setImportBusy(true);
    setImportError(null);
    try {
      const labelsToCreate = [
        ...new Set(
          importValid
            .filter((r) => r.categoryId === CREATE_CATEGORY_SENTINEL_ID)
            .map((r) => r.categoryLabel),
        ),
      ];
      const { categories: nextCategories, byLabel: createdByLabel } =
        labelsToCreate.length > 0
          ? await ensureCategoriesFromLabels(labelsToCreate, categories)
          : { categories, byLabel: new Map<string, number>() };
      if (labelsToCreate.length > 0) setCategories(nextCategories);

      for (const r of importValid) {
        const categoryId =
          r.categoryId === CREATE_CATEGORY_SENTINEL_ID
            ? createdByLabel.get(r.categoryLabel.toLowerCase())!
            : r.categoryId!;
        await putBudget({
          month: monthStart(r.month!),
          categoryId,
          minUsd: r.minUsd,
          maxUsd: r.maxUsd,
          notes: r.notes,
        });
      }
      setImportOpen(false);
      setImportText('');
      await load();
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setImportBusy(false);
    }
  }

  async function onCopyPrevious() {
    const prevMonthIso = monthStart(addMonths(month, -1));
    if (
      !window.confirm(
        `Copy budgets from ${monthLabel(addMonths(month, -1))} into ${monthLabel(month)}? Existing values for this month are overwritten.`,
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const prev = await fetchBudgets(prevMonthIso);
      for (const budget of prev) {
        await putBudget({
          month: monthIso,
          categoryId: budget.categoryId,
          minUsd: budget.minUsd ?? 0,
          maxUsd: budget.maxUsd ?? 0,
          notes: budget.notes,
        });
      }
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to copy budgets');
    } finally {
      setBusy(false);
    }
  }

  function includeCategory(categoryId: number, draft?: Draft) {
    setVisibleIds((prev) => new Set(prev).add(categoryId));
    setDrafts((prev) => {
      if (prev.has(categoryId) && !draft) return prev;
      return new Map(prev).set(categoryId, draft ?? { min: '', max: '', notes: '' });
    });
  }

  async function addCategory() {
    const newLabel = addNewLabel.trim();
    if (!newLabel && addExistingId === '') {
      setAddError('Pick an existing category or enter a new one.');
      return;
    }
    setAddBusy(true);
    setAddError(null);
    try {
      if (newLabel) {
        const { categories: nextCategories, byLabel } = await ensureCategoriesFromLabels(
          [newLabel],
          categories,
          addNewKind,
        );
        setCategories(nextCategories);
        const id = byLabel.get(newLabel.toLowerCase());
        if (id == null) {
          setAddError('Could not create that category.');
          return;
        }
        includeCategory(id);
      } else {
        includeCategory(Number(addExistingId));
      }
      setAddOpen(false);
      setAddExistingId('');
      setAddNewLabel('');
      setAddNewKind('expense');
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Failed to add category');
    } finally {
      setAddBusy(false);
    }
  }

  async function requestRemove(categoryId: number, label: string) {
    setBusy(true);
    setError(null);
    try {
      const usage = await fetchCategoryUsage(categoryId);
      if (usage.childCount > 0) {
        setError(
          `Can't remove "${label}" — it still has ${usage.childCount} subcategor${
            usage.childCount === 1 ? 'y' : 'ies'
          }. Remove those first.`,
        );
        return;
      }
      setRemoveTarget({ id: categoryId, label, transactionCount: usage.transactionCount });
      setRemoveOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to check category');
    } finally {
      setBusy(false);
    }
  }

  async function confirmRemove() {
    if (!removeTarget) return;
    setBusy(true);
    setError(null);
    try {
      await deleteCategory(removeTarget.id);
      const categoryId = removeTarget.id;
      setCategories((prev) => prev.filter((c) => c.id !== categoryId));
      setBudgets((prev) => {
        const next = new Map(prev);
        next.delete(categoryId);
        return next;
      });
      setVisibleIds((prev) => {
        const next = new Set(prev);
        next.delete(categoryId);
        return next;
      });
      setDrafts((prev) => {
        const next = new Map(prev);
        next.delete(categoryId);
        return next;
      });
      setRemoveOpen(false);
      setRemoveTarget(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove category');
    } finally {
      setBusy(false);
    }
  }

  return (
    <View>
      <View className="mb-3 flex-row items-center gap-2">
        <Text variant="body-sm" className="text-muted">
          Budget bands are in USD. Transfer categories are not budgeted.
        </Text>
        <View className="flex-1" />
        <Button
          label="Add category"
          size="sm"
          variant="primary"
          disabled={busy}
          onPress={() => {
            setAddError(null);
            setAddExistingId('');
            setAddNewLabel('');
            setAddNewKind('expense');
            setAddOpen(true);
          }}
        />
        <Button
          label="Import"
          size="sm"
          variant="secondary"
          disabled={busy}
          onPress={() => {
            setImportError(null);
            setImportOpen(true);
          }}
        />
        <Button
          label="Copy from previous month"
          size="sm"
          variant="secondary"
          disabled={busy}
          onPress={() => void onCopyPrevious()}
        />
      </View>

      {error ? (
        <Text variant="body-sm" className="mb-3 text-danger">
          {error}
        </Text>
      ) : null}

      {!loading && unbudgeted.length > 0 ? (
        <View className="mb-4 rounded-lg border border-warning bg-warning-muted p-3">
          <Text variant="body-sm" className="mb-1 font-semibold text-fg">
            Unbudgeted spend this month · {formatUsd(unbudgetedTotal)}
          </Text>
          <Text variant="caption" className="mb-2 text-fg-muted">
            These categories have spend but no budget covering them. Add one to start tracking it —
            we pre-fill the cap with what was spent.
          </Text>
          <View className="flex-col">
            {unbudgeted.map((row, index) => (
              <View
                key={row.categoryId}
                className={`flex-row items-center gap-2 py-1 ${
                  index > 0 ? 'border-t border-warning' : ''
                }`}
              >
                <Text variant="body-sm" className="flex-1 text-fg">
                  {row.label}
                </Text>
                <Text variant="body-sm" className="w-28 text-right tabular-nums text-fg-muted">
                  {formatUsd(row.actualUsd)}
                </Text>
                <Button
                  label="Add budget"
                  size="sm"
                  variant="secondary"
                  disabled={busy}
                  onPress={() => void quickBudget(row.categoryId, row.actualUsd)}
                />
              </View>
            ))}
          </View>
        </View>
      ) : null}

      {loading ? (
        <ActivityIndicator />
      ) : rows.length === 0 ? (
        <Text variant="body" className="text-muted">
          No budget categories this month. Add a category or copy from the previous month.
        </Text>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse text-sm">
            <thead>
              <tr className="text-fg-muted">
                <th className="border-b border-border px-3 py-2 text-left font-medium">Category</th>
                <th className="border-b border-border px-3 py-2 text-right font-normal text-fg-subtle">
                  {monthLabelShort(addMonths(month, -1))}
                </th>
                <th className="border-b border-border px-3 py-2 text-right font-medium">
                  Min (USD)
                </th>
                <th className="border-b border-border px-3 py-2 text-right font-medium">
                  Max (USD)
                </th>
                <th className="border-b border-border px-3 py-2 text-right font-normal text-fg-subtle">
                  {monthLabelShort(addMonths(month, 1))}
                </th>
                <th className="border-b border-border px-3 py-2 text-left font-medium">Notes</th>
                <th className="border-b border-border w-8 px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((option) => {
                const isChild = option.label.includes(' - ');
                const draft = drafts.get(option.id) ?? { min: '', max: '', notes: '' };
                return (
                  <tr key={option.id} className="group">
                    <td className="border-b border-border px-3 py-1">
                      <span className={isChild ? 'pl-4 text-fg-muted' : 'font-medium'}>
                        {option.label}
                        {option.kind === 'income' ? (
                          <span className="ml-1 text-[11px] text-fg-subtle">(income)</span>
                        ) : null}
                      </span>
                    </td>
                    <td className="border-b border-border px-3 py-1 text-right">
                      <span
                        className="tabular-nums text-xs text-fg-subtle"
                        title={prevBudgets.get(option.id)?.notes ?? undefined}
                      >
                        {bandText(prevBudgets.get(option.id))}
                      </span>
                    </td>
                    <td className="border-b border-border px-3 py-1 text-right">
                      <input
                        className="w-24 rounded border border-transparent px-2 py-1 text-right hover:border-border focus:border-accent"
                        inputMode="decimal"
                        placeholder="0"
                        value={draft.min}
                        onChange={(e) => setDraft(option.id, { min: e.target.value })}
                        onBlur={() => void commit(option.id)}
                      />
                    </td>
                    <td className="border-b border-border px-3 py-1 text-right">
                      <input
                        className="w-24 rounded border border-transparent px-2 py-1 text-right hover:border-border focus:border-accent"
                        inputMode="decimal"
                        placeholder="0"
                        value={draft.max}
                        onChange={(e) => setDraft(option.id, { max: e.target.value })}
                        onBlur={() => void commit(option.id)}
                      />
                    </td>
                    <td className="border-b border-border px-3 py-1 text-right">
                      <span
                        className="tabular-nums text-xs text-fg-subtle"
                        title={nextBudgets.get(option.id)?.notes ?? undefined}
                      >
                        {bandText(nextBudgets.get(option.id))}
                      </span>
                    </td>
                    <td className="border-b border-border px-3 py-1">
                      <input
                        className="w-56 rounded border border-transparent px-2 py-1 hover:border-border focus:border-accent"
                        value={draft.notes}
                        onChange={(e) => setDraft(option.id, { notes: e.target.value })}
                        onBlur={() => void commit(option.id)}
                      />
                    </td>
                    <td className="border-b border-border px-1 py-1">
                      <button
                        type="button"
                        className="flex h-7 w-7 items-center justify-center rounded text-fg-subtle opacity-0 transition-opacity hover:bg-danger-muted hover:text-danger focus-visible:opacity-100 group-hover:opacity-100"
                        aria-label={`Remove ${option.label}`}
                        disabled={busy}
                        onClick={() => void requestRemove(option.id, option.label)}
                      >
                        <TrashIcon />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog
        open={importOpen}
        title="Import budgets"
        onClose={() => setImportOpen(false)}
        footer={
          <>
            <DialogButton
              label="Cancel"
              variant="secondary"
              onPress={() => setImportOpen(false)}
            />
            <DialogButton
              label={importBusy ? 'Importing…' : `Import ${importValid.length}`}
              variant="primary"
              disabled={importBusy || importValid.length === 0}
              onPress={() => void runBudgetImport()}
            />
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <span className="cs-text-caption text-muted">
            Paste rows straight from Google Sheets (tab-separated) including the header. Expected
            columns: <span className="font-medium">Month, Category, Min (USD), Max (USD), Notes</span>{' '}
            — any extra columns are ignored. Categories use the{' '}
            <span className="font-medium">Parent - Child</span> label; new ones are added to Master
            Data automatically.
          </span>
          <textarea
            className="cs-input h-56 font-mono text-sm"
            placeholder={
              'Month\tCategory\tMin (USD)\tMax (USD)\tNotes\n2026-4\tBusiness - Ole\t1,200\t1,500\t'
            }
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
          />
          {importText.trim() ? (
            <span className="cs-text-caption text-muted">
              {importValid.length} budget{importValid.length === 1 ? '' : 's'} ready
              {importNewCategories > 0
                ? ` · ${importNewCategories} new categor${importNewCategories === 1 ? 'y' : 'ies'}`
                : ''}
              {importSkipped > 0 ? ` · ${importSkipped} row(s) skipped` : ''}.
            </span>
          ) : null}
          {importSkipped > 0 ? (
            <span className="cs-text-caption text-fg-muted">
              {importResolved.rows
                .filter((r) => r.errors.length > 0)
                .slice(0, 4)
                .map((r) => `Row ${r.index + 1}: ${r.errors.join(', ')}`)
                .join(' · ')}
              {importSkipped > 4 ? ' …' : ''}
            </span>
          ) : null}
          {importError ? (
            <span className="cs-text-caption text-danger">{importError}</span>
          ) : null}
        </div>
      </Dialog>

      <Dialog
        open={addOpen}
        title="Add budget category"
        onClose={() => setAddOpen(false)}
        footer={
          <>
            <DialogButton
              label="Cancel"
              variant="secondary"
              onPress={() => setAddOpen(false)}
            />
            <DialogButton
              label={addBusy ? 'Adding…' : 'Add'}
              variant="primary"
              disabled={addBusy || (!addNewLabel.trim() && addExistingId === '')}
              onPress={() => void addCategory()}
            />
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {addable.length > 0 ? (
            <label className="flex flex-col gap-1">
              <span className="cs-text-caption text-muted">Existing category</span>
              <select
                className="cs-input"
                value={addExistingId}
                disabled={addNewLabel.trim() !== ''}
                onChange={(e) => setAddExistingId(e.target.value)}
              >
                <option value="">Select…</option>
                {addable.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                    {option.kind === 'income' ? ' (income)' : ''}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <span className="cs-text-caption text-muted">
              Every existing expense/income category is already on this month.
            </span>
          )}
          <label className="flex flex-col gap-1">
            <span className="cs-text-caption text-muted">
              Or create a new one (Parent - Child). New categories are added to Master Data.
            </span>
            <div className="flex flex-row gap-2">
              <input
                className="cs-input flex-1"
                placeholder="Housing - Utilities"
                value={addNewLabel}
                onChange={(e) => setAddNewLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void addCategory();
                }}
              />
              <select
                className="cs-input w-32 shrink-0"
                value={addNewKind}
                onChange={(e) => setAddNewKind(e.target.value as FinanceCategoryKind)}
              >
                <option value="expense">expense</option>
                <option value="income">income</option>
              </select>
            </div>
          </label>
          {addError ? <span className="cs-text-caption text-danger">{addError}</span> : null}
        </div>
      </Dialog>

      <Dialog
        open={removeOpen}
        title="Remove category"
        onClose={() => {
          setRemoveOpen(false);
          setRemoveTarget(null);
        }}
        footer={
          <>
            <DialogButton
              label="Cancel"
              variant="secondary"
              onPress={() => {
                setRemoveOpen(false);
                setRemoveTarget(null);
              }}
            />
            <DialogButton
              label={busy ? 'Removing…' : 'Remove'}
              variant="danger"
              disabled={busy || !removeTarget}
              onPress={() => void confirmRemove()}
            />
          </>
        }
      >
        {removeTarget ? (
          <div className="flex flex-col gap-2">
            <span className="cs-text-body-sm text-fg">
              {categoryDeleteWarning(removeTarget.label, removeTarget.transactionCount)}
            </span>
            {removeTarget.transactionCount > 0 ? (
              <span className="cs-text-caption text-warning">
                Those transactions stay in the ledger with no category.
              </span>
            ) : null}
          </div>
        ) : null}
      </Dialog>
    </View>
  );
}
